import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { useQuery, type QueryClient } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { SearchX } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Chip } from "@/components/vroom/chip";
import { UserIdentityCell } from "@/components/ui/avatar";
import { ConfirmDialog } from "@/components/vroom/confirm-dialog";
import { ResponsiveDialog } from "@/components/responsive-dialog";
import { AdminShell } from "@/components/layout/admin-shell";
import { splitName } from "@/lib/split-name";
import {
  IndexPageHeader,
  IndexPageHeaderTitle,
  IndexPageHeaderToolbar,
} from "@/components/vroom/index-page-header";
import { SearchBar } from "@/components/vroom/search-bar";
import { DataTable } from "@/components/wrapped/data-table";
import { EmptyState } from "@/components/vroom/empty-state";
import { appSidebarSections, appBottomNavItems } from "@/lib/app-sidebar";
import { usersListOptions } from "@/api/users/query-options";
import { userReplacementsListOptions } from "@/api/user-replacements/query-options";
import { useDeleteReplacement } from "@/api/user-replacements/mutations";
import { buildReplacementRows, type ReplacementRow } from "@/lib/build-replacement-rows";
import { ReplacementAddPopover } from "@/components/admin/replacement-add-popover";

export const Route = createFileRoute("/_authed/admin/replacements")({
  beforeLoad: ({ context }) => {
    const user = (context as { user?: { role?: string } }).user;
    if (user?.role !== "super_admin") throw redirect({ to: "/dashboard" });
  },
  loader: ({ context }) => {
    const qc = (context as { queryClient: QueryClient }).queryClient;
    void qc.prefetchQuery(usersListOptions({ role: "approver", page: 1, pageSize: 200 }));
    void qc.prefetchQuery(usersListOptions({ role: "super_admin", page: 1, pageSize: 200 }));
    void qc.prefetchQuery(userReplacementsListOptions());
  },
  component: ReplacementsAdminPage,
});

interface SessionUser {
  name: string;
  email: string;
  role?: "worker" | "approver" | "super_admin";
}

export function ReplacementsAdminPage() {
  const { t } = useTranslation();
  const { user: sessionUser } = Route.useRouteContext() as { user: SessionUser };
  const { firstName, lastName } = splitName(sessionUser.name);
  const approvers = useQuery(usersListOptions({ role: "approver", page: 1, pageSize: 200 }));
  const supers = useQuery(usersListOptions({ role: "super_admin", page: 1, pageSize: 200 }));
  const reps = useQuery(userReplacementsListOptions());

  const [search, setSearch] = useState("");
  const [pendingDelete, setPendingDelete] = useState<{
    id: string;
    replacementName: string;
  } | null>(null);
  const [historyForId, setHistoryForId] = useState<string | null>(null);
  const deleteMut = useDeleteReplacement();

  const rows = useMemo(
    () =>
      buildReplacementRows(
        approvers.data?.data ?? [],
        supers.data?.data ?? [],
        reps.data?.data ?? [],
      ),
    [approvers.data, supers.data, reps.data],
  );

  const filteredRows = rows.filter((r) =>
    r.approver.name.toLowerCase().includes(search.toLowerCase()),
  );

  const allCandidates = useMemo(() => {
    const merged = new Map<string, { id: string; name: string; email: string }>();
    for (const r of rows)
      merged.set(r.approver.id, {
        id: r.approver.id,
        name: r.approver.name,
        email: r.approver.email,
      });
    return [...merged.values()];
  }, [rows]);

  const columns: ColumnDef<ReplacementRow, unknown>[] = [
    {
      id: "approver",
      accessorFn: (row) => row.approver.name,
      header: t("admin.replacements.columns.approver.header"),
      cell: (info) => {
        const u = info.row.original.approver;
        return <UserIdentityCell name={u.name} email={u.email} />;
      },
    },
    {
      id: "replacements",
      header: t("admin.replacements.columns.replacements.header"),
      cell: (info) => {
        const row = info.row.original;
        return (
          <div className="flex flex-wrap items-center gap-1">
            {row.replacements.map((r) => (
              <Chip
                key={r.id}
                removable
                onRemove={() =>
                  setPendingDelete({
                    id: r.id,
                    replacementName: r.replacement.name,
                  })
                }
                onClick={() =>
                  setPendingDelete({
                    id: r.id,
                    replacementName: r.replacement.name,
                  })
                }
                aria-label={t("admin.replacements.remove-chip.label", {
                  name: r.replacement.name,
                })}
              >
                {r.replacement.name}
              </Chip>
            ))}
            <ReplacementAddPopover
              approverId={row.approver.id}
              existingReplacementIds={row.replacements.map((r) => r.replacementId)}
              candidates={allCandidates}
            />
          </div>
        );
      },
    },
    {
      id: "status",
      accessorFn: (row) => row.status,
      header: t("admin.replacements.columns.status.header"),
      cell: (info) => {
        const status = info.row.original.status;
        return (
          <span
            className={
              status === "actief" ? "text-[12.5px] text-ink-700" : "text-[12.5px] text-vroom-red"
            }
          >
            {status === "actief"
              ? t("admin.replacements.columns.status.active")
              : t("admin.replacements.columns.status.no-coverage")}
          </span>
        );
      },
    },
    {
      id: "actions",
      header: "",
      cell: (info) => (
        <Button variant="ghost" onClick={() => setHistoryForId(info.row.original.approver.id)}>
          {t("admin.replacements.history.actions.open.label")}
        </Button>
      ),
    },
  ];

  return (
    <>
      <AdminShell
        user={{ firstName, lastName, role: "super_admin" }}
        section="admin"
        breadcrumb={<>{t("admin.replacements.breadcrumb")}</>}
        sidebarSections={appSidebarSections({ active: "replacements", role: "super_admin" }, t)}
        bottomNavItems={appBottomNavItems(
          { section: "admin", active: "replacements", role: "super_admin" },
          t,
        )}
      >
        <IndexPageHeader>
          <IndexPageHeaderTitle
            title={t("admin.replacements.title")}
            kicker={t("admin.replacements.kicker-total", { count: rows.length })}
          />
          <IndexPageHeaderToolbar>
            <div className="rounded-3 border border-line-strong bg-surface-1 px-3 py-2 text-[12.5px] text-ink-700">
              <strong>{t("admin.replacements.audit-rule.lead")}</strong>
              {t("admin.replacements.audit-rule.body")}{" "}
              <code>{t("admin.replacements.audit-rule.field")}</code>.
            </div>
            <SearchBar value={search} onChange={(e) => setSearch(e.target.value)} />
          </IndexPageHeaderToolbar>
        </IndexPageHeader>
        <div className="px-4 py-4 md:px-6 md:py-6">
          {filteredRows.length === 0 ? (
            <EmptyState
              variant="filtered"
              icon={<SearchX size={20} />}
              title={t("admin.replacements.empty.title")}
              description={t("admin.replacements.empty.description")}
            />
          ) : (
            <DataTable data={filteredRows} columns={columns} />
          )}
        </div>
      </AdminShell>

      <ConfirmDialog
        open={!!pendingDelete}
        onOpenChange={(open) => !open && setPendingDelete(null)}
        title={t("admin.replacements.delete-confirm.title")}
        description={
          pendingDelete
            ? t("admin.replacements.delete-confirm.description", {
                name: pendingDelete.replacementName,
              })
            : ""
        }
        destructiveLabel={t("admin.replacements.delete-confirm.destructive.label")}
        onDestructive={() => {
          if (pendingDelete) deleteMut.mutate({ id: pendingDelete.id });
          setPendingDelete(null);
        }}
        safeLabel={t("common.buttons.cancel.label")}
        onSafe={() => setPendingDelete(null)}
      />
      <ResponsiveDialog
        open={!!historyForId}
        onOpenChange={(open) => !open && setHistoryForId(null)}
        title={t("admin.replacements.history.title")}
        description={t("admin.replacements.history.description")}
      >
        <p className="text-[13px] text-ink-700">{t("admin.replacements.history.body")}</p>
      </ResponsiveDialog>
    </>
  );
}
